import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Download, FileText, Eye, Printer } from "lucide-react";
import { Button, Card } from "@samklang/ui";
import type { Perspective } from "@samklang/schemas";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { buildKartlaggningPdfDocument, generateKartlaggningPdf, type KartlaggningPdfSelection, type KartlaggningPdfType } from "@/services/kartlaggning-pdf";
import { exportPdf } from "@/services/pdf-export";
import { formatDate, perspectiveLabel } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

const TYPES: readonly { value: KartlaggningPdfType; label: string; description: string }[] = [
  { value: "full", label: "Fullständigt underlag", description: "Alla frågor och svar per perspektiv." },
  { value: "sammanfattning", label: "Sammanfattat underlag", description: "Centrala skattningar, styrkor, svårigheter, behov, skillnader, samstämmighet och insatser." },
  { value: "usk", label: "USK-underlag", description: "Strukturerat underlag per perspektiv och samlad bild." },
];

const allPerspectives = (): readonly Perspective[] => ["elev", "mentor", "vardnadshavare", "annan"];

export function KartlaggningPdfScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const all = useKartlaggningStore((store) => store.listKartlaggningar)();

  const runPerspectives = useMemo(() => (kartlaggning ? kartlaggning.runs.map((run) => run.perspective) : allPerspectives()), [kartlaggning]);

  const [type, setType] = useState<KartlaggningPdfType>("full");
  const [exportState, setExportState] = useState<"idle" | "exporting" | "done" | "error">("idle");
  const [selection, setSelection] = useState<KartlaggningPdfSelection>(() => ({
    perspectives: runPerspectives,
    includeAnswers: true,
    includeRatings: true,
    includeCompilation: true,
    includeStrengthsNeeds: true,
    includeActions: true,
    includeFollowUps: true,
    includeDevelopment: true,
    includeProvenance: true,
  }));

  const document = useMemo(
    () => (kartlaggning ? buildKartlaggningPdfDocument({ kartlaggning, history: all, type, selection }) : undefined),
    [kartlaggning, all, type, selection],
  );

  if (!kartlaggning || !document) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card><h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1></Card>
      </section>
    );
  }

  const togglePerspective = (perspective: Perspective) =>
    setSelection((current) => ({
      ...current,
      perspectives: current.perspectives.includes(perspective)
        ? current.perspectives.filter((item) => item !== perspective)
        : [...current.perspectives, perspective],
    }));

  const download = async () => {
    setExportState("exporting");
    try {
      const bytes = generateKartlaggningPdf(document);
      await exportPdf({
        bytes,
        fileName: `kartlaggning-${kartlaggning.participantReference || "elev"}-${type}.pdf`,
        title: `${kartlaggning.title} - ${document.subtitle}`,
      });
      setExportState("done");
    } catch {
      setExportState("error");
    }
  };

  const toggleItem = (checked: boolean, key: keyof KartlaggningPdfSelection) => {
    if (typeof selection[key] === "boolean") setSelection((current) => ({ ...current, [key]: checked }));
  };

  return (
    <section className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <h1 className="mt-3 flex items-center gap-2 font-display text-3xl font-semibold"><FileText className="text-primary" /> Generera PDF</h1>
      <p className="mt-2 text-foreground-muted">Förhandsvisa och ladda ner ett utskrifts- och mötesklart underlag.</p>

      <div className="mt-6 grid items-start gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card padding="md">
            <h2 className="font-display text-lg font-semibold">Typ av underlag</h2>
            <div className="mt-3 space-y-2">
              {TYPES.map((option) => (
                <label key={option.value} className="relative block">
                  <input type="radio" name="pdf-type" value={option.value} checked={type === option.value} onChange={() => setType(option.value)} className="peer sr-only" />
                  <span className="block rounded-2xl border border-border bg-surface p-3 peer-checked:border-primary peer-checked:bg-primary-soft">
                    <strong className="block text-sm">{option.label}</strong>
                    <span className="text-xs text-foreground-muted">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <Card padding="md">
            <h2 className="font-display text-lg font-semibold">Innehåll</h2>
            <div className="mt-3 space-y-3">
              <div>
                <p className="mb-2 text-sm font-semibold text-foreground-muted">Perspektiv</p>
                {allPerspectives().map((perspective) => (
                  <label key={perspective} className="flex min-h-11 items-center gap-2 text-sm">
                    <input type="checkbox" checked={selection.perspectives.includes(perspective)} onChange={() => togglePerspective(perspective)} className="h-5 w-5" />
                    {perspectiveLabel(perspective)}
                  </label>
                ))}
              </div>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeAnswers} onChange={(event) => toggleItem(event.target.checked, "includeAnswers")} className="h-5 w-5" /> Frågor + fullständiga svar</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeRatings} onChange={(event) => toggleItem(event.target.checked, "includeRatings")} className="h-5 w-5" /> Skattningar</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeCompilation} onChange={(event) => toggleItem(event.target.checked, "includeCompilation")} className="h-5 w-5" /> Sammanställning / skillnader</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeStrengthsNeeds} onChange={(event) => toggleItem(event.target.checked, "includeStrengthsNeeds")} className="h-5 w-5" /> Styrkor + behov</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeActions} onChange={(event) => toggleItem(event.target.checked, "includeActions")} className="h-5 w-5" /> Insatser + överenskommelser</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeFollowUps} onChange={(event) => toggleItem(event.target.checked, "includeFollowUps")} className="h-5 w-5" /> Uppföljningar</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeDevelopment} onChange={(event) => toggleItem(event.target.checked, "includeDevelopment")} className="h-5 w-5" /> Utveckling över tid</label>
              <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={selection.includeProvenance} onChange={(event) => toggleItem(event.target.checked, "includeProvenance")} className="h-5 w-5" /> Närvaro / källhänvisning</label>
            </div>
          </Card>
        </div>

        <div className="min-w-0">
          <Card padding="none" className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-border/60 bg-surface-muted px-5 py-3">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Eye size={19} className="text-primary" /> Förhandsvisning</h2>
              <Button onClick={() => void download()} disabled={exportState === "exporting"}>
                <Download size={19} /> {exportState === "exporting" ? "Förbereder PDF..." : "Spara eller dela PDF"}
              </Button>
            </div>
            <div className="max-h-[36rem] overflow-y-auto p-6">
              <article className="rounded-lg border border-border bg-white p-8 text-black shadow-sm">
                <h1 className="font-display text-2xl font-bold">{document.title}</h1>
                <p className="mt-1 text-sm">{document.subtitle}</p>
                <dl className="mt-4 grid gap-1 text-xs">
                  {document.meta.map((meta) => (
                    <div key={meta.label} className="flex gap-2">
                      <dt className="w-40 shrink-0 font-bold">{meta.label}</dt>
                      <dd>{meta.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-6 space-y-5">
                  {document.sections.map((section) => (
                    <section key={section.heading}>
                      <h2 className="border-b border-black/20 pb-1 text-sm font-bold uppercase">{section.heading}</h2>
                      <div className="mt-2 space-y-1 text-xs">
                        {section.lines.length === 0 ? <p className="text-black/50">Inget innehåll.</p> : section.lines.map((line, index) => <p key={index} className="whitespace-pre-wrap leading-relaxed">{line || "\u00A0"}</p>)}
                      </div>
                    </section>
                  ))}
                </div>
                <p className="mt-6 text-[10px] text-black/40">Genererad {formatDate(document.generatedAt)}.</p>
              </article>
              <p className="mt-4 flex items-center gap-2 text-xs text-foreground-muted"><Printer size={14} /> Anpassad för utskrift och möten.</p>
              <p className="mt-2 text-xs text-foreground-muted" aria-live="polite">
                {exportState === "done" ? "PDF-filen är klar." : exportState === "error" ? "PDF-filen kunde inte skapas. Försök igen." : ""}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
